import Header from "./header";
import Footer from "./footer";
import Home from "./home";
import SignUp from "./signup";
import LogIn from "./login";
import Pricing from "./pricing";
import Translate from "./translate";
import NotFound from "./notfound";
import Verify from "./verify";
import Reset from "./reset";
import Terms from "./terms";
import Privacy from "./privacy";
import Tokutei from "./tokutei";
import Thanks from "./thanks";
import { SignInStatus } from "./states";
import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Box } from "@mui/material";
import axios from "axios";
import { useConversionTracking } from "./conversionTracking";
import {
  AuthStatus,
  getAuthStatus,
  refreshAuthStatus,
  getAccessToken,
} from "./services/authService";

// ユーザー情報の型定義
export interface User {
  uid: string;
  email: string;
}

const App = () => {
  const [signInStatus, setSignInStatus] = useState(SignInStatus.INITIALSTATE);
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState("INITIALSTATE");
  const [expire, setExpire] = useState(0);

  const applyAuthStatus = useCallback(async (status: AuthStatus) => {
    if (!status.signedIn) {
      setSignInStatus(SignInStatus.NOTSIGNEDIN);
      setUser(null);
      setAccessToken("none");
      setExpire(0);
      return;
    }

    setUser({
      uid: status.uid!,
      email: status.email!,
    });

    if (!status.emailVerified) {
      setSignInStatus(SignInStatus.VERYFING);
      setAccessToken("none");
      setExpire(0);
      return;
    }

    if (status.status === "purchasing") {
      setSignInStatus(SignInStatus.PURCHASING);
      setAccessToken("none");
      setExpire(0);
      return;
    }

    if (status.status === "premium") {
      setSignInStatus(SignInStatus.PREMIUM);
      setExpire(status.expire || 0);
      const tokenResult = await getAccessToken();
      if (tokenResult.ok && tokenResult.accessToken) {
        setAccessToken(tokenResult.accessToken);
      } else {
        setAccessToken("none");
      }
      return;
    }

    setSignInStatus(SignInStatus.NOTSIGNEDIN);
    setAccessToken("none");
    setExpire(0);
  }, []);

  // 認証状態を取得する関数
  const fetchAuthStatus = useCallback(async () => {
    const status = await getAuthStatus();
    await applyAuthStatus(status);
  }, [applyAuthStatus]);

  // 認証状態を更新する関数（外部から呼び出せるようにエクスポート）
  const refreshStatus = useCallback(async () => {
    const status = await refreshAuthStatus();
    await applyAuthStatus(status);
  }, [applyAuthStatus]);

  // 初期化時に認証状態を取得
  useEffect(() => {
    fetchAuthStatus();
  }, [fetchAuthStatus]);

  // 広告のクリック識別子を UID に紐づけ、検知された購入を GA4 へ送る
  useConversionTracking(user?.uid);

  // Chrome拡張機能向けのアクセストークン通知
  useEffect(() => {
    if (accessToken === "INITIALSTATE") {
      return () => {};
    }
    function notification() {
      window.postMessage({ accessToken }, "*");
    }
    const notificationIntervalId = setInterval(notification, 1000);
    return () => {
      clearInterval(notificationIntervalId);
    };
  }, [accessToken]);

  // Cookie設定
  useEffect(() => {
    axios.post(
      process.env.REACT_APP_READABLE_BACKEND_URL + "set-cookie/",
      {},
      { withCredentials: true },
    );
  }, []);

  return (
    <BrowserRouter>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Header
          signInStatus={signInStatus}
          ticketPremium={expire !== 0}
          onLogout={fetchAuthStatus}
        />
        <Routes>
          <Route path="/" element={<Home signInStatus={signInStatus} />} />
          <Route
            path="/login"
            element={
              <LogIn
                signInStatus={signInStatus}
                onLoginSuccess={fetchAuthStatus}
              />
            }
          />
          <Route
            path="/signup"
            element={
              <SignUp
                signInStatus={signInStatus}
                user={user}
                setSignInStatus={setSignInStatus}
                setExpire={setExpire}
                onSignUpSuccess={fetchAuthStatus}
                onRefreshStatus={refreshStatus}
              />
            }
          />
          <Route
            path="/pricing"
            element={<Pricing signInStatus={signInStatus} />}
          />
          <Route
            path="/translate"
            element={
              <Translate
                signInStatus={signInStatus}
                user={user}
                expire={expire}
                accessToken={accessToken}
              />
            }
          />
          <Route
            path="/verify"
            element={
              <Verify
                signInStatus={signInStatus}
                setSignInStatus={setSignInStatus}
                onRefreshStatus={refreshStatus}
              />
            }
          />
          <Route path="/reset" element={<Reset />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/tokutei" element={<Tokutei />} />
          <Route path="/thanks" element={<Thanks />} />
          <Route path="/*" element={<NotFound />} />
        </Routes>
        <Footer />
      </Box>
    </BrowserRouter>
  );
};

export default App;
